import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef, useCallback } from 'react';
import { Button, Modal } from 'antd';
import { CloseOutlined, ArrowRightOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import './OnboardingTour.css';

const TOUR_STEPS = [
    {
        target: '.header-instrument-search',
        title: 'Step 1: Choose Symbol',
        content: 'Select the instrument you want to trade.',
        placement: 'bottom'
    },
    {
        target: '.conceptgate-pipeline',
        title: 'Step 2: Review Validation Pipeline',
        content: 'Review the validation gates and rules for the selected instrument.',
        placement: 'left'
    },
    {
        target: '.conceptgate-empty-state',
        title: 'Step 3: Create Trade & Validate',
        content: 'Create a trade manually here or on the chart, then run the validation pipeline.',
        placement: 'left'
    }
];

const OnboardingTour = forwardRef(({
    darkMode,
    onCategoryChange,
    onInstrumentChange,
    selectedCategory,
    selectedInstrument,
    onTourStart
}, ref) => {
    const [isTourActive, setIsTourActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [isWelcomeModalVisible, setIsWelcomeModalVisible] = useState(false);
    const overlayRef = useRef(null);
    const tooltipRef = useRef(null);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

    useEffect(() => {
        // Check if user has completed tour
        const hasCompletedTour = localStorage.getItem('ntb:copilot:tourCompleted');
        if (!hasCompletedTour) {
            // Show welcome modal after a short delay
            const timer = setTimeout(() => {
                setIsWelcomeModalVisible(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const removeHighlight = useCallback(() => {
        // Remove highlight from all potential target elements including fallbacks
        const selectors = [
            ...TOUR_STEPS.map(step => step.target),
            '.conceptgate-section',
            '.pipeline-collapsed-view',
            '.graph-container',
            '.middle-panel'
        ];

        selectors.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach(element => {
                element.classList.remove('ntb-tour-highlight');
            });
        });
    }, []);

    const addHighlight = useCallback((stepIndex) => {
        const step = TOUR_STEPS[stepIndex];
        let targetElement = document.querySelector(step.target);

        if (stepIndex === 0 && !targetElement) {
            targetElement = document.querySelector('.instrument-search') ||
                document.querySelector('.instrument-search-trigger');
        }

        // For step 2 (pipeline), try alternative selectors if main one not found
        if (stepIndex === 1 && !targetElement) {
            targetElement = document.querySelector('.conceptgate-section') ||
                document.querySelector('.pipeline-collapsed-view');
        }

        // For step 3 (create trade), try alternative selectors if main one not found
        // Use graph container as fallback for chart-based creation
        if (stepIndex === 2 && !targetElement) {
            targetElement = document.querySelector('.graph-container');
        }

        if (targetElement) {
            // Remove highlight from previous step
            removeHighlight();

            // Add highlight to current target
            targetElement.classList.add('ntb-tour-highlight');

            // Scroll element into view if needed
            targetElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest'
            });
            return true;
        } else {
            // Element not found - remove any existing highlights
            removeHighlight();
            return false;
        }
    }, [removeHighlight]);

    const updateTooltipPosition = useCallback((stepIndex) => {
        const delay = stepIndex === 1 ? 800 : 100; // Longer delay for chart area step
        setTimeout(() => {
            const step = TOUR_STEPS[stepIndex];
            let targetElement = document.querySelector(step.target);

            // For step 2 (pipeline), try alternative selectors if main one not found
            if (stepIndex === 1 && !targetElement) {
                targetElement = document.querySelector('.conceptgate-section') ||
                    document.querySelector('.pipeline-collapsed-view');
            }

            // For step 3 (create trade), try alternative selectors if main one not found
            if (stepIndex === 2 && !targetElement) {
                targetElement = document.querySelector('.graph-container');
            }

            if (targetElement) {
                // Scroll element into view first
                targetElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });

                // Wait for scroll to complete before calculating position
                setTimeout(() => {
                    const rect = targetElement.getBoundingClientRect();
                    const viewportHeight = window.innerHeight;
                    const viewportWidth = window.innerWidth;
                    const tooltipHeight = 220; // Approximate tooltip height
                    const tooltipWidth = 320; // Approximate tooltip width
                    const spacing = 16;
                    const margin = 20; // Margin from viewport edges

                    let top, left;
                    let placement = step.placement;

                    // Check if there's enough space below for bottom placement
                    const spaceBelow = viewportHeight - rect.bottom;
                    const spaceAbove = rect.top;

                    // Override placement for Chart fallback in Step 3
                    if (stepIndex === 2 && targetElement && (
                        targetElement.classList.contains('graph-container') ||
                        targetElement.classList.contains('middle-panel')
                    )) {
                        placement = 'top';
                    } else if (stepIndex === 1 && spaceBelow < tooltipHeight + spacing && spaceAbove > tooltipHeight + spacing) {
                        // For step 2, prefer top if not enough space below (existing logic)
                        placement = 'top';
                    }

                    switch (placement) {
                        case 'bottom':
                            top = rect.bottom + spacing;
                            // Check if tooltip would go below viewport
                            if (top + tooltipHeight > viewportHeight - margin) {
                                // Position above instead
                                top = rect.top - tooltipHeight - spacing;
                                if (top < margin) {
                                    // Center vertically if neither fits
                                    top = Math.max(margin, (viewportHeight - tooltipHeight) / 2);
                                }
                            }
                            left = rect.left + (rect.width / 2);
                            break;
                        case 'top':
                            top = rect.top - tooltipHeight - spacing;
                            // Check if tooltip would go above viewport
                            if (top < margin) {
                                // Position below instead
                                top = rect.bottom + spacing;
                                if (top + tooltipHeight > viewportHeight - margin) {
                                    // Center vertically if neither fits
                                    top = Math.max(margin, (viewportHeight - tooltipHeight) / 2);
                                }
                            }
                            left = rect.left + (rect.width / 2);
                            break;
                        case 'left':
                            top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
                            left = rect.left - tooltipWidth - spacing;
                            break;
                        case 'right':
                            top = rect.top + (rect.height / 2) - (tooltipHeight / 2);
                            left = rect.right + spacing;
                            break;
                        default:
                            top = rect.bottom + spacing;
                            left = rect.left + (rect.width / 2);
                    }

                    // Ensure tooltip stays within viewport horizontally
                    if (left - tooltipWidth / 2 < margin) {
                        left = tooltipWidth / 2 + margin;
                    } else if (left + tooltipWidth / 2 > viewportWidth - margin) {
                        left = viewportWidth - tooltipWidth / 2 - margin;
                    }

                    // Ensure tooltip stays within viewport vertically
                    if (top < margin) {
                        top = margin;
                    } else if (top + tooltipHeight > viewportHeight - margin) {
                        top = viewportHeight - tooltipHeight - margin;
                    }

                    // Use fixed positioning (relative to viewport, not document)
                    setTooltipPosition({ top, left });

                    // Add highlight to target element
                    addHighlight(stepIndex);
                }, 300); // Wait for scroll animation
            } else {
                // Element not found - position tooltip in center of viewport
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                setTooltipPosition({
                    top: viewportHeight / 2,
                    left: viewportWidth / 2
                });
                removeHighlight();
            }
        }, delay);
    }, [addHighlight, removeHighlight]);

    const startTour = useCallback(async () => {
        setIsWelcomeModalVisible(false);

        // Notify parent component that tour is starting
        if (onTourStart) {
            onTourStart();
        }

        // Auto-select demo instrument if none is selected
        if (!selectedInstrument && onCategoryChange && onInstrumentChange) {
            // Select FOREX category if not already selected
            if (selectedCategory !== 'FOREX') {
                onCategoryChange('FOREX');
                // Wait a bit for category to update
                await new Promise(resolve => setTimeout(resolve, 300));
            }

            // Select EUR/USD as demo instrument
            const demoInstrument = 'EUR_USD';
            onInstrumentChange(demoInstrument);

            // Wait for chart to load before starting tour
            await new Promise(resolve => setTimeout(resolve, 1000));
        }

        setIsTourActive(true);
        setCurrentStep(0);
        updateTooltipPosition(0);
    }, [selectedInstrument, selectedCategory, onCategoryChange, onInstrumentChange, onTourStart, updateTooltipPosition]);

    // Expose startTour method to parent component
    useImperativeHandle(ref, () => ({
        startTour: startTour
    }), [startTour]);

    const skipTour = () => {
        setIsWelcomeModalVisible(false);
        localStorage.setItem('ntb:copilot:tourCompleted', 'true');
    };

    const nextStep = () => {
        if (currentStep < TOUR_STEPS.length - 1) {
            const next = currentStep + 1;
            setCurrentStep(next);
            updateTooltipPosition(next);
        } else {
            completeTour();
        }
    };

    const prevStep = () => {
        if (currentStep > 0) {
            const prev = currentStep - 1;
            setCurrentStep(prev);
            updateTooltipPosition(prev);
        }
    };

    const completeTour = () => {
        removeHighlight();
        setIsTourActive(false);
        setCurrentStep(0);
        localStorage.setItem('ntb:copilot:tourCompleted', 'true');
    };

    useEffect(() => {
        if (isTourActive) {
            updateTooltipPosition(currentStep);

            const handleResize = () => updateTooltipPosition(currentStep);
            window.addEventListener('resize', handleResize);

            return () => {
                window.removeEventListener('resize', handleResize);
                removeHighlight();
            };
        } else {
            removeHighlight();
        }
    }, [isTourActive, currentStep, updateTooltipPosition, removeHighlight]);

    const currentStepData = TOUR_STEPS[currentStep];
    const progress = ((currentStep + 1) / TOUR_STEPS.length) * 100;

    return (
        <>
            {/* Welcome Modal */}
            <Modal
                open={isWelcomeModalVisible}
                onCancel={skipTour}
                footer={null}
                closable={true}
                className={`ntb-welcome-modal ${darkMode ? 'dark' : 'light'}`}
                width={500}
            >
                <div className={`ntb-welcome-modal-content ${darkMode ? 'dark' : 'light'}`}>
                    <h2>Welcome to NanoTrader Copilot!</h2>
                    <p>Get started with a quick tour of the key features.</p>
                    <div className="ntb-welcome-modal-actions">
                        <Button onClick={skipTour} size="large">
                            Skip Tour
                        </Button>
                        <Button type="primary" onClick={startTour} size="large" icon={<ArrowRightOutlined />}>
                            Start Tour
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Tour Overlay */}
            {isTourActive && (
                <>
                    <div
                        ref={overlayRef}
                        className="ntb-tour-overlay"
                        onClick={completeTour}
                    />

                    {currentStepData && (
                        <div
                            ref={tooltipRef}
                            className={`ntb-tour-tooltip ${darkMode ? 'dark' : 'light'}`}
                            style={{
                                top: `${tooltipPosition.top}px`,
                                left: `${tooltipPosition.left}px`,
                                transform: 'translateX(-50%)'
                            }}
                        >
                            <div className="ntb-tour-progress">
                                <div
                                    className="ntb-tour-progress-bar"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>

                            <div className="ntb-tour-tooltip-header">
                                <h3>{currentStepData.title}</h3>
                                <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    onClick={completeTour}
                                    className="ntb-tour-close"
                                />
                            </div>

                            <p className="ntb-tour-tooltip-content">{currentStepData.content}</p>

                            <div className="ntb-tour-tooltip-footer">
                                <span className="ntb-tour-step-indicator">
                                    {currentStep + 1} of {TOUR_STEPS.length}
                                </span>
                                <div className="ntb-tour-navigation">
                                    <Button
                                        onClick={prevStep}
                                        disabled={currentStep === 0}
                                        icon={<ArrowLeftOutlined />}
                                    >
                                        Previous
                                    </Button>
                                    <Button
                                        type="primary"
                                        onClick={nextStep}
                                        icon={currentStep === TOUR_STEPS.length - 1 ? null : <ArrowRightOutlined />}
                                    >
                                        {currentStep === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </>
    );
});

OnboardingTour.displayName = 'OnboardingTour';

export default OnboardingTour;
